import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useForm } from "react-hook-form";
import { ArrowLeft, Pen, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '@/shared/hooks/useAuth';
import { useEffect, useState } from "react";
import { SwitchCustom } from '@/shared/components/Switch';
import {
  PermissionsTable,
  type PermissionSystemSection,
} from '@/pages/configuracion/roles/ModulesPermissionsTable';
import { rolesApi } from '@/features/configuracion/api/roles.api';
import {
  applyRolePermissionsToSections,
  mapPermissionsToSections,
  mapSectionsToPermissionKeys,
  findResourcesSinVista,
} from '@/pages/configuracion/roles/permissions.mapper';
import { userHasPermission } from '@/shared/lib/roleUtils';
import toast from "react-hot-toast";

const CrudRoles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isCreate = location.pathname.includes("crear");
  const isEdit = location.pathname.includes("editar");
  const isView = location.pathname.includes("consultar");

  const searchParams = new URLSearchParams(location.search);
  const roleId = searchParams.get("id");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      status: true,
    },
  });

  const [permissionsModules, setPermissionsModules] = useState<
    PermissionSystemSection[]
  >([]);

  /**
   *  Carga TODOS los permisos existentes
   */
  const loadBasePermissions = async (): Promise<PermissionSystemSection[]> => {
    const token = localStorage.getItem("access_token");
    if (!token) return [];

    // rolesApi.getPermissions debería regresar un array de systems (o {data: systems})
    const res: any = await rolesApi.getPermissions(token);

    // const baseSections = mapPermissionsToSections(res.data);
    const baseSections = mapPermissionsToSections(res);

    const sortedSections = [...baseSections].sort((a, b) => {
      const rank = (key: string) => {
        if (key === "generales") return 0;
        if (key === "rh") return 1;
        if (key === "proveedores") return 99;
        return 2;
      };
      const diff = rank(a.key) - rank(b.key);
      return diff !== 0 ? diff : a.title.localeCompare(b.title);
    });

    setPermissionsModules(sortedSections);
    return sortedSections;
  };

  const onSubmit = async (data: any) => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const sinVista = findResourcesSinVista(permissionsModules);
    if (sinVista.length) {
      toast.error(
        `Marca al menos un permiso de Ver en: ${sinVista.join(", ")}. Con solo Crear o Editar el usuario no puede entrar al módulo.`,
      );
      return;
    }

    const permissionKeys = mapSectionsToPermissionKeys(permissionsModules);

    try {
      if (isCreate) {
        await rolesApi.create(
          {
            name: data.name,
            description: data.description,
            isActive: data.status,
            permissionKeys,
          },
          token,
        );

        navigate("/admin/roles");
      }

      if (isEdit && roleId) {
        await rolesApi.update(
          roleId,
          {
            name: data.name,
            description: data.description,
            isActive: data.status,
            permissionKeys,
          },
          token,
        );

        navigate("/admin/roles");
      }
    } catch (error) {
      console.error("Error guardando rol:", error);
      toast.error(
        "Error guardando rol. Por favor, intenta de nuevo mas tarde o contacta al administrador.",
      );
    }
  };

  useEffect(() => {
    const init = async () => {
      const baseSections = await loadBasePermissions();

      if ((isEdit || isView) && roleId && baseSections.length) {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const role = await rolesApi.getById(roleId, token);

        reset({
          name: role.name,
          description: role.description,
          status: role.isActive ?? true,
        });

        if (role.permissions?.length) {
          const updated = applyRolePermissionsToSections(
            baseSections,
            role.permissions,
          );
          setPermissionsModules(updated);
        }
      }
    };

    init();
  }, [isEdit, isView, roleId]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between my-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/roles")}
            className={estiloDeBoton('secundario', 'mb-6')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>

          <div>
            <h1 className="text-xl font-semibold text-[#1E1E1F]">
              {isCreate && "Crear Nuevo Rol"}
              {isEdit && "Editar Rol"}
              {isView && "Consultar Rol"}
            </h1>
            <p className="text-[#1E1E1F] text-sm">
              {isCreate && "Agrega un nuevo rol al sistema"}
              {isEdit && "Modifica la información del rol"}
              {isView && "Visualiza la información del rol"}
            </p>
          </div>
        </div>

        {isView &&
          userHasPermission(
            "generales.catalogos.roles.update",
            user?.rbac?.effectivePermissions || [],
          ) && (
            <button
              onClick={() => navigate(`/roles/editar?id=${roleId}`)}
              className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
            >
              <Pen className="h-4 w-4" />
              <span>Editar</span>
            </button>
          )}
      </div>

      {/* Form */}
      <div className="bg-white border border-[#E0E0E0] rounded-xl p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Info */}
          <div className="border border-[#E0E0E0] rounded-xl">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-[#1E1E1F]">
                Información del rol
              </h2>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Nombre *
                </label>
                <input
                  disabled={isView}
                  {...register("name", {
                    required: !isView ? "Dato requerido." : false,
                  })}
                  className={`w-full px-3 py-2 rounded-lg bg-[#F6F9FB]
                    ${isView ? "opacity-60 cursor-not-allowed" : ""}
                    ${errors.name ? "ring-2 ring-red-500" : "border-0"}
                  `}
                  placeholder="Ingresa el nombre"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Estatus */}
              <div className="flex items-center">
                <SwitchCustom
                  value={watch("status")}
                  onChange={(e) => setValue("status", e)}
                  disabled={isView}
                />
                <span className="ml-2 text-sm">
                  {watch("status") ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>

            {/* Descripción */}
            <div className="px-6 pb-6">
              <label className="block text-sm font-medium mb-2">
                Descripción
              </label>
              <textarea
                disabled={isView}
                {...register("description")}
                className={`w-full px-3 py-2 rounded-lg bg-[#F6F9FB]
                  ${isView ? "opacity-60 cursor-not-allowed" : ""}
                `}
                rows={4}
                placeholder="Ingresa la descripción"
              />
            </div>
          </div>

          {/* Permissions */}
          <div className="my-12">
            <PermissionsTable
              data={permissionsModules}
              readOnly={isView}
              onChange={setPermissionsModules}
            />
          </div>

          {/* Actions */}
          {!isView && (
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate("/admin/roles")}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className={estiloDeBoton('primario')}
              >
                <Save className="w-4 h-4" />
                {isCreate ? "Crear rol" : "Guardar cambios"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CrudRoles;
